import type { JobCategorySettings } from '@utils/businessCategoryUtils';
import type { TransactionType } from '@prisma/client';
import { DocumentType, PDFType } from '@prisma/client';
import type { Decimal } from '@prisma/client/runtime/library';
import { DateTime } from 'luxon';
import type { EstimateGroupFull, EstimateWithGroups } from '@services/estimate.service';
import type { BusinessWithAddress } from '@services/quoteRequest.service';
import type { IAddress } from '~/hooks/useFormAddress';
import type { IEstimateForQuote } from '~/react_context/EstimateQuoteContext';
import type {
	IQuoteFormValues,
	IQuoteItem,
	IQuotePercentLineItem,
	QuoteDefaults,
	QuoteEstimateLineItemAndEstimateLineItem,
	QuoteGroupedEstimateLineItem,
	TQuoteFull,
} from '~/server/schema/quote.schema';

// The invoice-terms constants keep their long-standing import path here — both server code (FinanceCenter's
// effective-due-date helpers and their SQL twin, AccountingService aging, the dashboard's past-due card) and
// client copy explaining the rules read them from `@utils/quoteUtils`. They are defined in
// base.receivables.terms.ts, at the repo root, because glazier-agent-core's Mastra tools need them too and
// cannot import anything under src/ (no path aliases there, and this file's own imports would drag in the app's
// module graph). That module deliberately has no imports of its own so client components can keep reading these
// through here — see its header.
export { AIA_BILLING_TERM_DAYS, UNDATED_INVOICE_GRACE_DAYS } from '../../base.receivables.terms';

type PercentLineItemSource = {
	amount: Decimal | number | string | null;
	estimateGroupId?: number | null;
	id: number;
	name: string;
	percent: Decimal | number | string | null;
};

export const buildQuotePercentLineItems = (
	estimate:
		| {
				estimateGroups?: ({ estimatePercentLineItems?: PercentLineItemSource[] | null; id?: number } | null)[] | null;
				estimatePercentLineItems?: PercentLineItemSource[] | null;
		  }
		| null
		| undefined
): IQuotePercentLineItem[] => {
	if (!estimate) return [];

	const toItem = (item: PercentLineItemSource): IQuotePercentLineItem => ({
		amount: Number(item.amount ?? 0),
		estimateGroupId: item.estimateGroupId ?? null,
		id: item.id,
		name: item.name,
		percent: Number(item.percent ?? 0),
	});

	return [
		...(estimate.estimatePercentLineItems ?? []).map(toItem),
		...(estimate.estimateGroups ?? []).flatMap((group) => (group?.estimatePercentLineItems ?? []).map(toItem)),
	];
};
import { DateHelper } from './date.helper';
import { DEFAULT_DOCUMENT_LAYOUT_BLOCKS, parseBlockArray, resolveQuoteLayoutOrder } from './documentLayoutOrder';
import { stringifyEstimateLineItem } from './pdfUtils';
import { generateAddressString } from './stringHelpers';
import type { IQuoteView } from '~/components/PDF/QuotePDF';

export const isQuoteExpired = (expirationDate: Date | null | undefined, timezone: string): boolean => {
	if (!expirationDate) return false;
	const today = DateTime.now().setZone(timezone).startOf('day');
	const expiresOn = DateTime.fromJSDate(expirationDate).setZone(timezone).startOf('day');
	return today > expiresOn;
};

export const convertFormValuesForCreateQuote = ({
	form,
	estimateId,
	jobId,
	transactionType,
	isInvoice,
	emailSender,
	quoteItemsWithImg,
	quoteEstimateLineItems,
}: {
	form: IQuoteFormValues;
	estimateId?: number;
	jobId?: number;
	transactionType: TransactionType | null;
	isInvoice: boolean;
	emailSender?: string | null;
	quoteItemsWithImg?: IQuoteItem[];
	quoteEstimateLineItems: QuoteEstimateLineItemAndEstimateLineItem[];
}) => {
	return {
		number: form.number,
		name: form.name,
		estimateId,
		quoteEstimateLineItem: quoteEstimateLineItems,
		showItemPrices: form.showItemPrices,
		showGroupTax: form.showGroupTax,
		showQuantity: form.showQuantity,
		showLineItemPrices: form.showLineItemPrices || false,
		showCostPerUnit: form.showCostPerUnit,
		showInclusionsAndExclusionsOnFirstPage: form.showInclusionsAndExclusionsOnFirstPage,
		splitItemsOverPages: form.splitItemsOverPages,
		showTotalsBox: form.showTotalsBox,
		showOnlyTotal: form.showOnlyTotal,
		showTaxPercentage: form.showTaxPercentage,
		showCode: form.showCode,
		showDepositDue: form.showDepositDue,
		showExactMeasurements: form.showExactMeasurements,
		showGlassFabrications: form.showGlassFabrications,
		showEstimateLineItems: form.showEstimateLineItems ?? false,
		showHeaderTotal: form.showHeaderTotal,
		showLaborItems: form.showLaborItems,
		showApprovedChangeOrders: form.showApprovedChangeOrders,
		quoteItems: form.quoteItems?.map((item, index) => {
			return {
				name: item.name,
				estimateGroupId: item.estimateGroupId,
				content: item.content,
				totalPrice: item.totalPrice && +item.totalPrice,
				docs: quoteItemsWithImg?.[index]?.docs ?? [],
				isRequired: item.isRequired,
				quoteDescription: item.estimateGroup?.quoteContent,
				isChangeOrderItem: item.isChangeOrderItem,
				estimateLineItemType: item.estimateLineItemType,
				isHidden: item.isHidden,
			};
		}),
		depositPercent: form.depositPercent,
		tax: form.tax,
		subtotal: form.subtotal,
		total: form.total,
		jobId,
		note: form.note,
		terms: form.terms,
		emailSender,
		type: form.pdfType,
		isInvoice,
		transactionType,
		customerContact: form.customerContact,
		businessAddress: form.businessAddress,
		billTo: form.billTo,
		customerId: form.customer?.id,
		contacts: form.contacts,
		jobInfo: form.jobInfo,
		quoteDate: form.pdfType === PDFType.QUOTE ? form.quoteDate : undefined,
		invoiceDate: form.pdfType === PDFType.INVOICE ? form.invoiceDate : undefined,
		receiptDate: form.pdfType === PDFType.RECEIPT ? form.receiptDate : undefined,
		expirationDate: form.pdfType === PDFType.QUOTE ? form.expirationDate : undefined,
		dueDate: form.pdfType === PDFType.INVOICE ? form.dueDate : undefined,
		isDueUponReceipt: form.isDueUponReceipt,
		inclusions: form.inclusions,
		exclusions: form.exclusions,
	};
};

export const convertFormValuesForUpdateQuote = ({
	form,
	quoteId,
	transactionType,
	isInvoice,
	emailSender,
	quoteItemsWithImg,
	quoteEstimateLineItems,
}: {
	form: IQuoteFormValues;
	quoteId: number;
	transactionType: TransactionType | null;
	isInvoice: boolean;
	emailSender?: string | null;
	quoteItemsWithImg?: IQuoteItem[];
	quoteEstimateLineItems: QuoteEstimateLineItemAndEstimateLineItem[];
}) => {
	return {
		quote: {
			billTo: form.billTo,
			businessAddress: form.businessAddress,
			contacts: form.contacts,
			customerContact: form.customerContact,
			customerId: form.customer?.id,
			depositPercent: form.depositPercent,
			dueDate: form.pdfType === PDFType.INVOICE ? form.dueDate : undefined,
			emailSender,
			exclusions: form.exclusions,
			expirationDate: form.pdfType === PDFType.QUOTE ? form.expirationDate : undefined,
			id: quoteId,
			inclusions: form.inclusions,
			isDueUponReceipt: form.isDueUponReceipt,
			isInvoice,
			jobInfo: form.jobInfo,
			layoutId: form.layoutId,
			layoutOrderOverride: form.layoutOrderOverride,
			name: form.name,
			note: form.note,
			number: form.number,
			quoteDate: form.pdfType === PDFType.QUOTE ? form.quoteDate : undefined,
			invoiceDate: form.pdfType === PDFType.INVOICE ? form.invoiceDate : undefined,
			receiptDate: form.pdfType === PDFType.RECEIPT ? form.receiptDate : undefined,
			quoteEstimateLineItem: quoteEstimateLineItems,
			showApprovedChangeOrders: form.showApprovedChangeOrders,
			showCode: form.showCode,
			showCostPerUnit: form.showCostPerUnit,
			showDepositDue: form.showDepositDue,
			showEstimateLineItems: form.showEstimateLineItems ?? false,
			showExactMeasurements: form.showExactMeasurements,
			showGlassFabrications: form.showGlassFabrications,
			showGroupTax: form.showGroupTax,
			showHeaderTotal: form.showHeaderTotal,
			showInclusionsAndExclusionsOnFirstPage: form.showInclusionsAndExclusionsOnFirstPage,
			showItemPrices: form.showItemPrices,
			showLaborItems: form.showLaborItems,
			showLineItemPrices: form.showLineItemPrices || false,
			showOnlyTotal: form.showOnlyTotal,
			showQuantity: form.showQuantity,
			showTaxPercentage: form.showTaxPercentage,
			showTotalsBox: form.showTotalsBox,
			splitItemsOverPages: form.splitItemsOverPages,
			subtotal: form.subtotal,
			tax: form.tax,
			terms: form.terms,
			total: form.total,
			transactionType,
			type: form.pdfType,
		},
		quoteItems:
			form.quoteItems?.map((item, index) => {
				return {
					content: item.content,
					docs: quoteItemsWithImg?.[index]?.docs ?? [],
					estimateGroupId: item.estimateGroupId,
					estimateLineItemType: item.estimateLineItemType,
					id: item.id || undefined,
					isChangeOrderItem: item.isChangeOrderItem,
					isHidden: item.isHidden,
					isRequired: item.isRequired,
					name: item.name,
					quoteDescription: item.estimateGroup?.quoteContent,
					totalPrice: item.totalPrice ? +item.totalPrice : null,
				};
			}) || [],
	};
};

export const getQuoteDisplayProps = ({
	branchAddress,
	business,
	businessPhone,
	businessSettingForBusinessCategory,
	defaultEstimateAddressTemplate,
	depositAmount,
	emailSender,
	estimate,
	groupedQuoteEstimateLineItems,
	isChangeOrderView,
	overwriteCustomerContact,
	pdfLogoURL,
	quote,
	quoteDefaults,
	quoteItemsWithImg,
}: {
	branchAddress: IAddress | null;
	business: BusinessWithAddress;
	businessPhone?: string | null;
	businessSettingForBusinessCategory: JobCategorySettings;
	defaultEstimateAddressTemplate?: IAddress | null;
	depositAmount: number;
	emailSender: string;
	estimate: IEstimateForQuote | null;
	groupedQuoteEstimateLineItems: QuoteGroupedEstimateLineItem;
	isChangeOrderView: boolean;
	overwriteCustomerContact?: string;
	pdfLogoURL?: string | null;
	quote: TQuoteFull | null;
	quoteDefaults: QuoteDefaults;
	quoteItemsWithImg: IQuoteItem[];
}): IQuoteView => {
	return {
		balance: estimate?.balance,
		billTo: quote?.billTo ?? undefined,
		blockOrder: resolveQuoteLayoutOrder({
			docType: quote?.type,
			layoutBlocks: parseBlockArray(quote?.layout?.blocks) ?? DEFAULT_DOCUMENT_LAYOUT_BLOCKS,
			override: parseBlockArray(quote?.layoutOrderOverride),
		}),
		builderPoNumber: estimate?.jobBuilderPoNumber ?? null,
		business: business,
		businessAddress: getBusinessAddressForQuote({ branchAddress, business, defaultEstimateAddressTemplate, quote }),
		businessPhone,
		businessSettingForBusinessCategory,
		customerContact: overwriteCustomerContact ?? quote?.customerContact ?? '',
		date:
			(quote?.type === PDFType.INVOICE ? quote?.invoiceDate : quote?.type === PDFType.RECEIPT ? quote?.receiptDate : quote?.quoteDate) ??
			estimate?.createdAt ??
			undefined,
		depositAmount,
		depositPercent: estimate?.depositPercent ? +estimate.depositPercent : undefined,
		dueDate: quote?.dueDate ?? undefined,
		emailSender,
		estimatePercentLineItems: buildQuotePercentLineItems(estimate),
		estimateTransactions: estimate?.transactions,
		exclusions: quote?.exclusions ?? quoteDefaults.exclusions ?? undefined,
		expirationDate: quote?.expirationDate ?? undefined,
		inclusions: quote?.inclusions ?? quoteDefaults.inclusions ?? undefined,
		isChangeOrderView,
		isDueUponReceipt: quote?.isDueUponReceipt ?? false,
		jobContacts: !!overwriteCustomerContact ? undefined : quote?.contacts?.map((contact) => contact.contactDisplay),
		jobInfo: quote?.jobInfo,
		jobName: quote?.name ?? '',
		logoUrl: pdfLogoURL ?? undefined,
		note: quote?.note ?? '',
		paymentProcessingFee:
			estimate?.paymentProcessingFeeAmount && estimate?.paymentProcessingFeeAmount > 0 ? +estimate?.paymentProcessingFeeAmount : undefined,
		pdfType: quote?.type,
		quoteEstimateLineItem: groupedQuoteEstimateLineItems,
		quoteItems: quote?.quoteItems ? (quote.quoteItems as IQuoteItem[]) : quoteDefaults.quoteItems,
		quoteNumber: quote?.number ?? '',
		quoteItemsWithImg,
		showCode: quote?.showCode ?? false,
		showCostPerUnit: quote?.showCostPerUnit,
		showDepositDue: quote?.showDepositDue ?? true,
		showEstimateLineItems: quote?.showEstimateLineItems ?? quoteDefaults.showEstimateLineItems ?? true,
		showGroupTax: quote?.showGroupTax,
		showHeaderTotal: quote?.showHeaderTotal ?? true,
		showInclusionsAndExclusionsOnFirstPage: quote?.showInclusionsAndExclusionsOnFirstPage,
		showItemPrices: quote?.showItemPrices ?? quoteDefaults.showItemPrices ?? true,
		showLaborItems: quote?.showLaborItems ?? true,
		showLineItemPrices: quote?.showLineItemPrices ?? quoteDefaults.showLineItemPrices ?? false,
		showOnlyTotal: quote?.showOnlyTotal ?? false,
		showQuantity: quote?.showQuantity,
		showTaxPercentage: quote?.showTaxPercentage ?? false,
		showTotalsBox: quote?.showTotalsBox ?? true,
		splitItemsOverPages: quote?.splitItemsOverPages,
		subtotal: estimate?.subtotal ? +estimate.subtotal : undefined,
		tax: estimate?.tax ? +estimate.tax : undefined,
		taxPercentage: estimate?.taxPercent ? +estimate.taxPercent : undefined,
		terms: quote?.terms ?? '',
		total: estimate?.total ? +estimate.total : undefined,
	};
};

export const stringifyEstimateGroupLineItems = ({
	estimateGroup,
	includeLineItemPrices,
	showExactMeasurements = false,
}: {
	estimateGroup: EstimateGroupFull;
	includeLineItemPrices: boolean;
	showExactMeasurements?: boolean;
}) => {
	return estimateGroup.estimateLineItems?.reduce((stringAccumulator, estimateLineItem, index) => {
		if (index === 0) {
			return stringAccumulator + `${stringifyEstimateLineItem(estimateLineItem, includeLineItemPrices, showExactMeasurements)}`;
		} else {
			return stringAccumulator + `\n${stringifyEstimateLineItem(estimateLineItem, includeLineItemPrices, showExactMeasurements)}`;
		}
	}, '');
};

function quoteItemsFromEstimateGroups(estimateGroups: EstimateGroupFull[] | null): IQuoteItem[] | undefined {
	if (estimateGroups === null) {
		return;
	}

	return estimateGroups.map((estimateGroup) => {
		const estimateLineItemStrings = stringifyEstimateGroupLineItems({ estimateGroup, includeLineItemPrices: false });

		return {
			content: estimateLineItemStrings,
			estimateGroup: {
				...estimateGroup,
				tax: estimateGroup.tax === null ? null : +estimateGroup.tax,
			},
			estimateGroupId: estimateGroup.id,
			id: null,
			isChangeOrderItem: false,
			isIncludedInQuote: estimateGroup.isIncludedInQuote,
			isRequired: estimateGroup.isRequired,
			lineItemsDescription: estimateLineItemStrings,
			name: estimateGroup.name,
			quoteDescription: estimateGroup.quoteContent,
			totalPrice: Number(estimateGroup.totalPrice),
		};
	});
}

function zipEstimateGroupsAndQuoteItemsForChangeOrder(
	estimateGroups: EstimateGroupFull[] | null,
	quoteItems: IQuoteItem[] | null,
	showLineItemPrices: boolean
): IQuoteItem[] | undefined {
	const estimateGroupIds = estimateGroups?.map((estimateGroup) => estimateGroup.id);
	quoteItems = (quoteItems ?? []).filter(
		(quoteItem) =>
			quoteItem.estimateGroup?.deletedAt == null && quoteItem.estimateGroupId && estimateGroupIds?.includes(quoteItem.estimateGroupId)
	);

	if (quoteItems.length > 0) {
		const quoteItemEstimateGroupIds = quoteItems.map((quoteItem) => quoteItem.estimateGroupId);

		// Apply lineItemsDescription to QuoteItems
		quoteItems = quoteItems.map((quoteItem) => {
			if (quoteItem.estimateGroupId) {
				const estimateGroup = estimateGroups?.find((estimateGroup) => {
					return estimateGroup.id === quoteItem.estimateGroupId;
				});

				if (estimateGroup) {
					quoteItem.lineItemsDescription = stringifyEstimateGroupLineItems({ estimateGroup, includeLineItemPrices: showLineItemPrices });
				}
			}
			return quoteItem;
		});

		// Quote Items exist with estimate group IDs
		const quoteItemlessEstimateGroups = estimateGroups?.filter((estimateGroup) => {
			return !quoteItemEstimateGroupIds?.includes(estimateGroup?.id);
		});
		if (quoteItemlessEstimateGroups && quoteItemlessEstimateGroups.length > 0) {
			return [...quoteItems, ...(quoteItemsFromEstimateGroups(quoteItemlessEstimateGroups) ?? [])];
		}

		return quoteItems;
	}

	// No QuoteItems - turn estimate groups into quote items
	return quoteItemsFromEstimateGroups(estimateGroups);
}

export function zipEstimateGroupsAndQuoteItems(
	estimateGroups: EstimateGroupFull[] | null,
	quoteItems: IQuoteItem[] | null,
	showLineItemPrices: boolean,
	changeOrders?: EstimateWithGroups[]
): IQuoteItem[] {
	const changeOrderQuoteItems = changeOrders
		?.map((changeOrder) => {
			return zipEstimateGroupsAndQuoteItemsForChangeOrder(changeOrder.estimateGroups, quoteItems, showLineItemPrices);
		})
		.flat()
		.filter((quoteItem) => !!quoteItem)
		.map((quoteItem) => ({ ...quoteItem, isChangeOrderItem: true }));
	const changeOrderEstimateGroupIds = changeOrderQuoteItems?.map((quoteItem) => quoteItem.estimateGroupId);

	quoteItems = (quoteItems ?? []).filter(
		(quoteItem) => quoteItem.estimateGroup?.deletedAt == null && !changeOrderEstimateGroupIds?.includes(quoteItem.estimateGroupId)
	);

	if (quoteItems.length > 0) {
		const quoteItemEstimateGroupIds = quoteItems.map((quoteItem) => {
			return quoteItem.estimateGroupId;
		});

		// Apply lineItemsDescription to QuoteItems
		quoteItems = quoteItems.map((quoteItem) => {
			if (quoteItem.estimateGroupId) {
				const estimateGroup = estimateGroups?.find((estimateGroup) => {
					return estimateGroup.id === quoteItem.estimateGroupId;
				});

				if (estimateGroup) {
					quoteItem.lineItemsDescription = stringifyEstimateGroupLineItems({ estimateGroup, includeLineItemPrices: showLineItemPrices });
				}
			}
			return quoteItem;
		});

		// Quote Items exist with estimate group IDs
		const quoteItemlessEstimateGroups = estimateGroups?.filter((estimateGroup) => {
			return !quoteItemEstimateGroupIds?.includes(estimateGroup?.id);
		});
		if (quoteItemlessEstimateGroups && quoteItemlessEstimateGroups.length > 0) {
			quoteItems = [...quoteItems, ...(quoteItemsFromEstimateGroups(quoteItemlessEstimateGroups) ?? [])].sort(
				(a, b) => (a.estimateGroup?.order ?? 0) - (b.estimateGroup?.order ?? 0)
			);
		}

		return [...quoteItems, ...(changeOrderQuoteItems ?? [])];
	}

	// No QuoteItems - turn estimate groups into quote items
	return [...(quoteItemsFromEstimateGroups(estimateGroups) ?? []), ...(changeOrderQuoteItems ?? [])];
}

export function getDefaultAttachedFileName(documentType: DocumentType, timezone: string, quoteId?: number) {
	const date = DateHelper.getBusinessLocalNow(timezone).toJSON()?.slice(0, 10).replace(/-/g, '_');

	if (!quoteId || !date) return undefined;

	switch (documentType) {
		case DocumentType.QUOTE:
			return `QUOTE-${quoteId}-${date}.pdf`;
		case DocumentType.INVOICE:
			return `INVOICE-${quoteId}-${date}.pdf`;
		case DocumentType.RECEIPT:
			return `RECEIPT-${quoteId}-${date}.pdf`;
		default:
			return undefined;
	}
}

export function getBusinessAddressForQuote({
	branchAddress,
	business,
	defaultEstimateAddressTemplate,
	quote,
}: {
	branchAddress: IAddress | null;
	business: {
		address: IAddress | null;
	};
	defaultEstimateAddressTemplate?: IAddress | undefined | null;
	quote: { businessAddress: string | null | undefined } | null;
}) {
	if (quote?.businessAddress) return quote.businessAddress;
	if (branchAddress) return generateAddressString(branchAddress);
	if (defaultEstimateAddressTemplate) return generateAddressString(defaultEstimateAddressTemplate);
	return business.address !== null ? generateAddressString(business.address) : null;
}
